import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Modal,
  Typography,
  styled,
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faDownload,
  faCircleCheck,
  faSend,
} from '@fortawesome/pro-regular-svg-icons'
import { toast } from 'react-toastify'
import lightTheme from '../../styles/theme/lightTheme'
import Api from '../../services/api.service'
import { handlingErrors } from '../../utils'
import { useTranslation } from 'react-i18next'
import { BorderButtonPurple, CustomButton } from '../buttons'
import { useSelector } from 'react-redux'
import { FILE_URL } from '../../config'
import { useNavigate } from 'react-router-dom'

const ModalContainer = styled(Box)(({ theme }) => ({
  width: 'calc(100% - 50px)',
  height: 650,
  backgroundColor: '#FFFFFF',
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  padding: '30px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  rowGap: '20px',

  '&:focus-visible': {
    outline: 'none',
  },

  [theme.breakpoints.up('sm')]: {
    width: 700,
    /*height: 500,*/
    height: 'auto',
    rowGap: '35px',
  },
}))

const BoxContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: 500,
  backgroundColor: lightTheme.palette.primary.b6,
  padding: '20px',

  [theme.breakpoints.up('sm')]: {
    /*height: 300,*/
    height: 'auto',
  },
}))

const StyledListItem = styled(ListItem)(({ theme }) => ({
  border: `1px solid ${lightTheme.palette.primary.b6}`,
  borderRadius: '5px',
  marginBottom: '10px',
  '&:hover': {
    backgroundColor: lightTheme.palette.primary.b6,
  },
}))

const CvItemBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
}))

const DownloadButton = styled(Button)(({ theme }) => ({
  minWidth: 'auto',
  padding: '8px',
  backgroundColor: lightTheme.palette.primary.b2,
  color: '#fff',
  '&:hover': {
    backgroundColor: lightTheme.palette.primary.main,
  },
}))

const UserCvsModal = ({ open, onClose, besoinId }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const userData = useSelector((state) => state.userAuth.user)
  const [loading, setLoading] = useState(false)
  const [userCvs, setUserCvs] = useState([])
  const [checkedCvs, setCheckedCvs] = useState({})
  const [allChecked, setAllChecked] = useState(true)

  useEffect(() => {
    if (open && userData) {
      const docs = []
      const documents = Object.values(userData?.data?.documents)
      documents.forEach((doc) => {
        docs.push(doc)
      })

      setUserCvs(docs)
    }
  }, [open, userData])

  // Initialize all checkboxes to checked when CVs are loaded
  useEffect(() => {
    if (userCvs.length > 0) {
      const initialCheckedState = {}
      userCvs.forEach((cv, index) => {
        initialCheckedState[index] = true
      })
      setCheckedCvs(initialCheckedState)
      setAllChecked(true)
    }
  }, [userCvs])

  const handleDownloadCv = (cvUrl) => {
    // Implement CV download functionality
    window.open(cvUrl, '_blank')
  }

  const handleCheckboxChange = (index) => {
    setCheckedCvs((prev) => {
      const newState = { ...prev, [index]: !prev[index] }
      // Check if all items are checked
      const allChecked = Object.values(newState).every(
        (value) => value === true
      )
      setAllChecked(allChecked)
      return newState
    })
  }

  const handleCheckAll = () => {
    const newAllChecked = !allChecked
    setAllChecked(newAllChecked)

    const newCheckedState = {}
    userCvs.forEach((_, index) => {
      newCheckedState[index] = newAllChecked
    })
    setCheckedCvs(newCheckedState)
  }

  /* const handleSubmit = () => {
    // Get all checked CVs
    const selectedCvs = userCvs.filter((_, index) => checkedCvs[index])
    console.log('Selected CVs:', selectedCvs)
    // Implement your submit logic here
    toast.success(t('successMessage.cvsSent'), {
      position: 'top-right',
      autoClose: 3000,
      hideProgressBar: false,
    })
    onClose()
  } */

  const handleSubmit = async () => {
    const selectedCvs = userCvs.filter((_, index) => checkedCvs[index])
    
    //return
    setLoading(true)
    try {
      const result = await Api.candidate.postuler(besoinId, {
        cvs: selectedCvs,
      })
      const handling = handlingErrors(result)

      if (handling.ok) {
        toast.success(t('box.successPostuler'), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
          icon: ({ theme, type }) => (
            <FontAwesomeIcon
              icon={faCircleCheck}
              fontSize={25}
              color={lightTheme.palette.success.main}
            />
          ),
        })
      }
      setLoading(false)
      onClose()
    } catch (error) {
      setLoading(false)
    }
  }

  const hasCheckedCvs = () => {
    return Object.values(checkedCvs).some((value) => value === true)
  }

  return (
    <Modal
      open={open}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(51, 51, 51, 0.65)',
        },
      }}
    >
      <ModalContainer>
        <Typography variant={'subtitle2'} textTransform={'uppercase'}>
          {t('box.title.sendCv')}
        </Typography>
        {userCvs.length > 1 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              width: '100%',
              ml: 5,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  edge="start"
                  checked={allChecked}
                  onChange={handleCheckAll}
                  tabIndex={-1}
                  sx={{ mr: 1 }}
                />
              }
              label={t('button.selectAll')}
            />
          </Box>
        )}
        <BoxContainer>
          {userCvs.length > 0 ? (
            <List>
              {userCvs.map((cv, index) => (
                <StyledListItem key={index}>
                  <CvItemBox>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {/*  <Checkbox
                        checked={checkedCvs[index] || false}
                        onChange={() => handleCheckboxChange(index)}
                        icon={<FontAwesomeIcon icon={faSquare} />}
                        checkedIcon={<FontAwesomeIcon icon={faSquareCheck} />}
                        sx={{ mr: 1 }}
                      /> */}

                      <Checkbox
                        edge="start"
                        checked={checkedCvs[index] || false}
                        tabIndex={-1}
                        sx={{ mr: 1 }}
                        onChange={() => handleCheckboxChange(index)}
                      />

                      <ListItemText primary={`${cv.fichier}`} />
                    </Box>
                    {cv.fichier && (
                      <DownloadButton
                        variant="contained"
                        onClick={() =>
                          handleDownloadCv(
                            `${FILE_URL}${cv.fichier.split('/').pop()}`
                          )
                        }
                      >
                        <FontAwesomeIcon
                          icon={faDownload}
                          fontSize={16}
                          color="#fff"
                        />
                      </DownloadButton>
                    )}
                  </CvItemBox>
                </StyledListItem>
              ))}
            </List>
          ) : (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '50px',
                }}
              >
                <Typography>{t('errorMessage.noCvsFound')}</Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '50px',
                }}
              >
                <Button
                  variant="contained"
                  onClick={() => navigate('/user')}
                  sx={{ mt: 2 }}
                >
                  {t('header.myProfil')}
                </Button>
              </Box>
            </>
          )}
        </BoxContainer>
        <Box className={'blockButtons'} sx={{ display: 'flex', gap: 2 }}>
          <BorderButtonPurple displayIcon={'none'} handleClick={onClose}>
            {t('button.cancel')}
          </BorderButtonPurple>
          {hasCheckedCvs() && (
            <CustomButton displayIcon={'none'} handleClick={handleSubmit}>
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                <>
                  {t('contact.send')} <FontAwesomeIcon icon={faSend} />
                </>
              )}
            </CustomButton>
          )}
        </Box>
      </ModalContainer>
    </Modal>
  )
}

export default UserCvsModal
